/** @jsxImportSource @emotion/react */
import useAppTheme from './useAppTheme';
import useColor from './useColor';

import {Global, css} from '@emotion/react';
import React from 'react';

/**
 * GlobalStyles component applies a set of global CSS styles to the application.
 *
 * This component leverages Emotion's `Global` component to define consistent
 * styling rules across the entire application, ensuring a unified look and feel.
 *
 * Styles applied include:
 * - Default body styles such as margin, font family, and text color.
 * - Global box-sizing rules for all elements.
 * - Focus-visible styles for accessibility on interactive elements.
 * - Font inheritance and margin reset for form elements.
 * - Consistent font smoothing and text rendering for better readability.
 * - Resetting list styles and heading margins.
 * - Neutralizing image and media display properties.
 * - Styling for links, tables, and buttons.
 *
 * The styles are dynamically derived from the theme using the `useTheme` hook,
 * ensuring they adapt to the application's design system.
 *
 * @returns A JSX element that applies global styles to the application.
 */
export const GlobalStyles = () => {
  const {colors, font, spacings} = useAppTheme();
  const {pallet} = useColor();

  return (
    <Global
      styles={css({
        // Set body default
        body: {
          margin: 0,
          fontFamily: font.family.sans,
          color: colors['brown-900'],
        },

        // Set hr style
        hr: {
          border: 0,
          height: spacings.px2,
          width: '100%',
          backgroundColor: pallet.background.neutral.defaultColor,
          margin: 0,
        },

        // Set settings globally
        '*, *::before, *::after': {
          boxSizing: 'border-box',
          color: 'inherit',
          font: 'inherit',
        },

        // Set focus by keyboard
        'a:focus-visible, button:focus-visible, input:focus-visible, textarea:focus-visible, select:focus-visible':
          {
            outlineColor: colors['pink-900'],
            outlineStyle: 'solid',
            outlineWidth: spacings.px1,
            outlineOffset: spacings.px3,
          },

        // Inherit fonts for form elements
        'input, button, textarea, select': {margin: 0},

        // Use consistent font smoothing
        html: {
          WebkitTextSizeAdjust: '100%',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          textRendering: 'optimizeLegibility',
          lineHeight: 1.5,
        },

        // Remove list styles
        'ol, ul': {
          listStyle: 'none',
          padding: 0,
          margin: 0,
        },

        // Neutralize image display
        'img, picture, video, canvas, svg': {
          display: 'block',
          maxWidth: '100%',
        },

        // Reset heading margins
        'h1, h2, h3, h4, h5, h6, p, figure': {margin: 0},

        // Link improvements
        a: {
          backgroundColor: 'transparent',
          color: 'inherit',
          textDecoration: 'none',
        },

        // Tables
        table: {borderCollapse: 'collapse', borderSpacing: 0},

        // Buttons
        button: {
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
        },
      })}
    />
  );
};
